import { Announcement, Badge, Button, Checkbox, IconButton, Layout, PinIcon } from '@utilities';

import { type TurfItem } from '../../utilities/adapter';
import { type TurfReachResult } from '../../utilities/algorithm';
import { formatPercent } from '../../utilities/format';
import { TURF_BASKET_STATES, type TurfBasketStateValue } from '../../utilities/state';

import styles from '../../_index.module.scss';

export interface TurfItemWithReach {
  item: TurfItem;
  reach: TurfReachResult;
}

interface ItemsPanelProps {
  basketItemIds: string[];
  basketState: TurfBasketStateValue;
  filteredRespondentCount: number;
  includeSpecialItems: boolean;
  isOptimizerDisabled: boolean;
  items: TurfItemWithReach[];
  maxSize: number;
  maxSizeLimit: number;
  onBasketItemToggle: (itemId: string) => void;
  onClearBasket: () => void;
  onFindBestSet: () => void;
  onIncludeSpecialItemsChange: (value: boolean) => void;
  onMaxSizeChange: (value: number) => void;
  onPinnedItemToggle: (itemId: string) => void;
  pinnedItemIds: string[];
}

const ItemsPanel = ({
  basketItemIds,
  basketState,
  filteredRespondentCount,
  includeSpecialItems,
  isOptimizerDisabled,
  items,
  maxSize,
  maxSizeLimit,
  onBasketItemToggle,
  onClearBasket,
  onFindBestSet,
  onIncludeSpecialItemsChange,
  onMaxSizeChange,
  onPinnedItemToggle,
  pinnedItemIds,
}: ItemsPanelProps) => (
  <Layout.Flex.Column className={styles['turf-simulator-panel']} gap="large">
    <div className={styles['turf-simulator-build-header']}>
      <h3>Build Basket</h3>
      <div className={styles['turf-simulator-panel-actions']}>
        <Button
          data-testid="turf-simulator-find-best-set-button"
          isDisabled={isOptimizerDisabled}
          onClick={onFindBestSet}
          text="Find best set"
          variant="primary"
        />
        <Button
          data-testid="turf-simulator-clear-basket-button"
          isDisabled={!basketItemIds.length && !pinnedItemIds.length}
          onClick={onClearBasket}
          text="Clear basket"
          variant="secondary"
        />
      </div>
      <Badge
        className={styles['turf-simulator-basket-badge']}
        label={getBasketStateLabel(basketState)}
        variant={basketState === TURF_BASKET_STATES.BEST_SET ? 'success' : 'default'}
      />
    </div>

    <div className={styles['turf-simulator-settings']}>
      <label className={styles['turf-simulator-max-size-field']}>
        <span>Max size</span>
        <input
          data-testid="turf-simulator-max-size-input"
          max={maxSizeLimit}
          min={1}
          onChange={(event) => onMaxSizeChange(Number(event.target.value))}
          type="number"
          value={maxSize}
        />
      </label>
      <Checkbox
        data-testid="turf-simulator-include-special-items-checkbox"
        id="turf-include-special-items"
        isChecked={includeSpecialItems}
        label="Include special items"
        onChange={() => onIncludeSpecialItemsChange(!includeSpecialItems)}
        value={includeSpecialItems}
      />
    </div>

    <div className={styles['turf-simulator-items-header']}>
      <h4>Items</h4>
      <span>{filteredRespondentCount} filtered respondents</span>
    </div>

    {items.length === 0 ? (
      <Announcement
        header="No visible items"
        text="Enable special items to show the available TURF options."
        variant="info"
      />
    ) : (
      <ul aria-label="TURF items" className={styles['turf-simulator-item-list']}>
        {items.map(({ item, reach }) => {
          const isPinned = pinnedItemIds.includes(item.id);
          const isSelected = basketItemIds.includes(item.id);

          return (
            <li key={item.id}>
              <Checkbox
                className={styles['turf-simulator-item-checkbox']}
                data-testid={`turf-simulator-item-checkbox-${item.id}`}
                id={`turf-item-${item.id}`}
                isChecked={isSelected}
                isDisabled={isPinned}
                label={
                  <div className={styles['turf-simulator-item-label']}>
                    <span>{item.label}</span>
                    {item.special && (
                      <span className={styles['turf-simulator-item-meta']}>Special</span>
                    )}
                  </div>
                }
                onChange={() => onBasketItemToggle(item.id)}
                value={item.id}
              />
              <span className={styles['turf-simulator-item-reach']}>
                {formatPercent(reach.pct)}
              </span>
              <IconButton
                aria-pressed={isPinned}
                className={isPinned ? styles['is-pinned'] : undefined}
                data-testid={`turf-simulator-pin-item-button-${item.id}`}
                onClick={() => onPinnedItemToggle(item.id)}
                tooltip={isPinned ? `Unpin ${item.label}` : `Pin ${item.label}`}
              >
                <PinIcon />
              </IconButton>
            </li>
          );
        })}
      </ul>
    )}
  </Layout.Flex.Column>
);

const getBasketStateLabel = (basketState: TurfBasketStateValue): string =>
  basketState === TURF_BASKET_STATES.BEST_SET ? 'Best set' : 'Custom basket';

export default ItemsPanel;
